import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { PageBanner, PAGE_BANNERS } from "@/components/PageBanner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, timeAgo, peso } from "@/lib/api";
import { propertyTypeLabel } from "@/lib/taxonomy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Handshake, Plus, Loader2, Search, UserCheck, Phone, Mail, MapPin, Building2, Send, Inbox, Lock, Wallet, BedDouble } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { BUYER_RENTER: "Buyer / Renter", PROPERTY_OWNER: "Property Owner" };
const STATUS_STYLE = {
  SUBMITTED: "bg-amber-50 text-amber-700", ACCEPTED: "bg-blue-50 text-blue-700",
  IN_PROGRESS: "bg-indigo-50 text-indigo-700", COMPLETED: "bg-emerald-50 text-emerald-700",
  DECLINED: "bg-red-50 text-red-600", CANCELLED: "bg-slate-100 text-slate-500",
};

function StatusPill({ s }) {
  return <span data-testid={`referral-status-${s}`} className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLE[s] || "bg-slate-100 text-slate-500"}`}>{(s || "").replace("_", " ")}</span>;
}

function ReferDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [pros, setPros] = useState([]);
  const [pro, setPro] = useState(null);
  const [f, setF] = useState({ referral_type: "BUYER_RENTER", lead_first_name: "", contact_mobile: "", contact_email: "", city_area: "", note: "", budget_min: "", budget_max: "", consent: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/referrals/professionals", { params: { q } }); setPros(data || []); } catch { setPros([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const reset = () => { setF({ referral_type: "BUYER_RENTER", lead_first_name: "", contact_mobile: "", contact_email: "", city_area: "", note: "", budget_min: "", budget_max: "", consent: false }); setPro(null); setQ(""); };

  const submit = async () => {
    if (!pro) { toast.error("Select a professional to refer to."); return; }
    if (!f.lead_first_name.trim()) { toast.error("Enter the lead's first name."); return; }
    if (!f.contact_mobile.trim() && !f.contact_email.trim()) { toast.error("Provide a mobile number or email."); return; }
    if (!f.consent) { toast.error("Please confirm you have permission to share this contact."); return; }
    setBusy(true);
    try {
      await api.post("/referrals", {
        professional_id: pro.id, referral_type: f.referral_type, lead_first_name: f.lead_first_name.trim(),
        contact_mobile: f.contact_mobile.trim(), contact_email: f.contact_email.trim(), city_area: f.city_area.trim(),
        note: f.note.trim(),
        budget_min: f.referral_type === "BUYER_RENTER" && f.budget_min ? Number(f.budget_min) : null,
        budget_max: f.referral_type === "BUYER_RENTER" && f.budget_max ? Number(f.budget_max) : null,
        consent: f.consent,
      });
      toast.success("Referral sent!");
      setOpen(false); reset(); onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="refer-opportunity-btn" className="bg-[#C79A3B] text-[#0F2C59] hover:bg-[#C79A3B]/90 font-semibold rounded-xl gap-2"><Plus className="h-4 w-4" /> Refer an Opportunity</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="refer-dialog">
        <DialogHeader><DialogTitle>Refer an Opportunity</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Referral type</Label>
            <Select value={f.referral_type} onValueChange={(v) => set("referral_type", v)}>
              <SelectTrigger data-testid="refer-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="BUYER_RENTER">Buyer / Renter — I know someone looking for a property</SelectItem>
                <SelectItem value="PROPERTY_OWNER">Property Owner — I know someone who may want to sell or lease</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Refer to professional</Label>
            {pro ? (
              <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-white px-3 h-11" data-testid="refer-pro-selected">
                <UserCheck className="h-4 w-4 text-primary" /><span className="text-sm font-medium truncate">{pro.name}</span>
                <button type="button" onClick={() => setPro(null)} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Change</button>
              </div>
            ) : (
              <div className="mt-1.5">
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input data-testid="refer-pro-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or city…" className="pl-9" /></div>
                <ul className="mt-1 max-h-44 overflow-auto rounded-xl border border-border divide-y" data-testid="refer-pro-list">
                  {pros.map((p) => (
                    <li key={p.id}><button type="button" data-testid={`refer-pro-${p.id}`} onClick={() => setPro(p)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                      <img src={p.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                      <span className="min-w-0"><span className="block font-medium truncate">{p.name}</span><span className="block text-xs text-slate-400 truncate">{p.role_label}{p.city ? ` · ${p.city}` : ""}</span></span>
                    </button></li>
                  ))}
                  {!pros.length && <li className="px-3 py-2 text-xs text-slate-400">No matching professionals.</li>}
                </ul>
              </div>
            )}
          </div>
          <div><Label>Lead first name</Label><Input data-testid="refer-name" value={f.lead_first_name} onChange={(e) => set("lead_first_name", e.target.value)} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mobile</Label><Input data-testid="refer-mobile" value={f.contact_mobile} onChange={(e) => set("contact_mobile", e.target.value)} placeholder="+63 9XX…" className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="refer-email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5" /></div>
          </div>
          <p className="text-xs text-slate-400 -mt-2">Provide at least one contact method.</p>
          <div><Label>City / Area</Label><Input data-testid="refer-city" value={f.city_area} onChange={(e) => set("city_area", e.target.value)} placeholder="e.g. Makati" className="mt-1.5" /></div>
          {f.referral_type === "BUYER_RENTER" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Budget min <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="refer-budget-min" type="number" value={f.budget_min} onChange={(e) => set("budget_min", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Budget max <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="refer-budget-max" type="number" value={f.budget_max} onChange={(e) => set("budget_max", e.target.value)} className="mt-1.5" /></div>
            </div>
          )}
          <div><Label>Short note / context</Label><Textarea data-testid="refer-note" value={f.note} onChange={(e) => set("note", e.target.value)} rows={3} className="mt-1.5" placeholder="What are they looking for / need help with?" /></div>
          <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
            <Checkbox data-testid="refer-consent" checked={f.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
            <span>I confirm that I have permission to share this person's contact information with the selected real estate professional through PropertyConnect.</span>
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="refer-submit" onClick={submit} disabled={busy} className="bg-[#0F2C59] text-white hover:bg-[#0F2C59]/90 font-semibold rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Referral"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartnerView() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/referrals/mine"); setData(data); } catch { setData({ referrals: [], counts: {} }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try { await api.post(`/referrals/${id}/cancel`); toast.success("Referral cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const c = data?.counts || {};
  const stats = [
    ["Active", (c.SUBMITTED || 0)], ["Accepted", (c.ACCEPTED || 0)],
    ["In Progress", (c.IN_PROGRESS || 0)], ["Completed", (c.COMPLETED || 0)],
  ];

  return (
    <PageShell testid="referrals-partner-page">
      <PageBanner
        testid="referrals-partner-banner"
        image={PAGE_BANNERS.referrals}
        icon={Handshake}
        title="My Referrals"
        description="Introduce buyers, owners and opportunities to trusted professionals."
        actions={<ReferDialog onCreated={load} />}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {stats.map(([label, n]) => (
          <div key={label} data-testid={`referral-stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-2xl font-bold text-[#0F2C59]">{n}</div>
            <div className="text-xs text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {data === null ? <p className="text-sm text-slate-400">Loading…</p>
        : !data.referrals.length ? (
          <div data-testid="referrals-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-14 text-center">
            <Handshake className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No referrals yet</p>
            <p className="text-sm text-slate-400 mt-1">Tap “Refer an Opportunity” to make your first introduction.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.referrals.map((r) => (
              <div key={r.id} data-testid={`referral-card-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0F2C59] text-white">{TYPE_LABEL[r.referral_type]}</span>
                    <StatusPill s={r.status} />
                  </div>
                  <span className="text-xs text-slate-400">{timeAgo(r.created_at)}</span>
                </div>
                <p className="text-sm font-semibold text-slate-900 mt-3">{r.lead_first_name}{r.city_area ? <span className="font-normal text-slate-500"> · {r.city_area}</span> : null}</p>
                {r.note && <p className="text-sm text-slate-600 mt-1">{r.note}</p>}
                <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
                  {r.professional && <span className="flex items-center gap-1.5"><UserCheck className="h-4 w-4 text-primary" /> Referred to <span className="font-medium">{r.professional.name}</span></span>}
                  {r.status === "SUBMITTED" && <button data-testid={`referral-cancel-${r.id}`} onClick={() => cancel(r.id)} className="ml-auto text-xs text-red-600 hover:underline">Cancel</button>}
                </div>
              </div>
            ))}
          </div>
        )}
    </PageShell>
  );
}

// Shared opportunity summary — prefers the immutable need_snapshot (professional referrals),
// falls back to legacy inline fields (partner referrals where need_snapshot is null).
function OpportunitySummary({ r }) {
  const s = r.need_snapshot;
  if (s) {
    const budget = (s.budget_min || s.budget_max) ? `${s.budget_min ? peso(s.budget_min) : "—"}–${s.budget_max ? peso(s.budget_max) : "—"}` : null;
    return (
      <div className="mt-3">
        <p className="text-sm font-semibold text-slate-900">{propertyTypeLabel(s.property_type)} · {s.listing_type === "Unit Hosting" ? "Stay" : s.listing_type}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-600">
          {s.cities?.length ? <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.cities.join(", ")}</span> : null}
          {budget && <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />{budget}</span>}
          {s.min_bedrooms ? <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{s.min_bedrooms}+ Bedrooms</span> : null}
        </div>
        {s.public_description && <p className="text-sm text-slate-500 mt-1.5">{s.public_description}</p>}
      </div>
    );
  }
  return (
    <div className="mt-3">
      <p className="text-sm font-semibold text-slate-900">{r.lead_first_name}{r.city_area ? <span className="font-normal text-slate-500"> · {r.city_area}</span> : null}</p>
      {(r.budget_min || r.budget_max) && <p className="text-xs text-slate-500 mt-0.5">Budget: {r.budget_min ? peso(r.budget_min) : "—"} – {r.budget_max ? peso(r.budget_max) : "—"}</p>}
    </div>
  );
}

function ReceivedTab() {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/referrals/inbox"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, path) => {
    try { await api.post(`/referrals/${id}/${path}`, {}); toast.success(path === "accept" ? "Referral accepted" : "Referral declined"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/referrals/${id}/status`, { status }); toast.success(`Marked ${status.replace("_", " ").toLowerCase()}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (items === null) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!items.length) return (
    <div data-testid="received-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-14 text-center">
      <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-600 font-medium">No referrals received yet</p>
      <p className="text-sm text-slate-400 mt-1">Client opportunities referred to you by other professionals will appear here.</p>
    </div>
  );
  return (
    <div className="space-y-3">
      {items.map((r) => {
        const from = r.referrer?.name || r.referral_partner?.name;
        const hasSharable = (r.shared_categories?.length || 0) > 0 || r.origin !== "PROFESSIONAL";
        return (
          <div key={r.id} data-testid={`received-card-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0F2C59] text-white">{TYPE_LABEL[r.referral_type] || "Buyer / Renter"}</span>
              <div className="flex items-center gap-2"><StatusPill s={r.status} /><span className="text-xs text-slate-400">{timeAgo(r.created_at)}</span></div>
            </div>
            {from && <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5"><UserCheck className="h-4 w-4 text-primary" /> Referred by <span className="font-semibold text-slate-800">{from}</span></p>}
            <OpportunitySummary r={r} />
            {r.note && <p className="text-sm text-slate-600 mt-2 italic">“{r.note}”</p>}
            {/* Contact availability */}
            <div className="mt-3 pt-3 border-t border-slate-100 text-sm">
              {r.status === "SUBMITTED" ? (
                <p className="text-xs text-slate-400 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />
                  {hasSharable ? "Contact details locked until acceptance" : "The referring professional did not share client contact details"}</p>
              ) : (
                <div className="flex flex-wrap items-center gap-4 text-slate-600">
                  {r.lead_first_name && <span className="font-medium text-slate-800">{r.lead_first_name}</span>}
                  {r.contact_mobile && <a href={`tel:${r.contact_mobile}`} className="flex items-center gap-1 text-slate-700"><Phone className="h-3.5 w-3.5" /> {r.contact_mobile}</a>}
                  {r.contact_email && <a href={`mailto:${r.contact_email}`} className="flex items-center gap-1 text-[#0F2C59]"><Mail className="h-3.5 w-3.5" /> {r.contact_email}</a>}
                  {!r.lead_first_name && !r.contact_mobile && !r.contact_email && <span className="text-xs text-slate-400">No client contact was shared for this referral.</span>}
                </div>
              )}
            </div>
            {r.status === "SUBMITTED" && (
              <p className="text-xs text-slate-400 mt-2">Accept to receive any client contact the referring professional authorized and create or link the opportunity in your Leads.</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {r.status === "SUBMITTED" && <>
                <Button data-testid={`received-accept-${r.id}`} onClick={() => act(r.id, "accept")} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">Accept</Button>
                <Button data-testid={`received-decline-${r.id}`} onClick={() => act(r.id, "decline")} variant="outline" className="h-9 rounded-lg">Decline</Button>
              </>}
              {r.status === "ACCEPTED" && <>
                {r.lead_id && <a href={`/leads/${r.lead_id}`} className="text-sm text-[#0F2C59] font-semibold hover:underline">View Lead →</a>}
                <Button data-testid={`received-inprogress-${r.id}`} onClick={() => setStatus(r.id, "IN_PROGRESS")} variant="outline" className="h-9 rounded-lg ml-auto">Mark In Progress</Button>
                <Button data-testid={`received-complete-${r.id}`} onClick={() => setStatus(r.id, "COMPLETED")} className="h-9 bg-[#0F2C59] text-white rounded-lg">Complete</Button>
              </>}
              {r.status === "IN_PROGRESS" && <>
                {r.lead_id && <a href={`/leads/${r.lead_id}`} className="text-sm text-[#0F2C59] font-semibold hover:underline">View Lead →</a>}
                <Button data-testid={`received-complete-${r.id}`} onClick={() => setStatus(r.id, "COMPLETED")} className="h-9 bg-[#0F2C59] text-white rounded-lg ml-auto">Complete</Button>
              </>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SentTab() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/referrals/sent"); setData(data); } catch { setData({ referrals: [], counts: {} }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try { await api.post(`/referrals/${id}/cancel`); toast.success("Referral cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (data === null) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!data.referrals.length) return (
    <div data-testid="sent-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-14 text-center">
      <Send className="h-10 w-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-600 font-medium">No referrals sent yet</p>
      <p className="text-sm text-slate-400 mt-1">Refer a client from one of your Buyer Needs to collaborate with another real estate professional.</p>
      <a href="/my-buyers" className="inline-block mt-4 text-sm text-[#0F2C59] font-semibold hover:underline">Go to My Buyer Needs →</a>
    </div>
  );
  return (
    <div className="space-y-3">
      {data.referrals.map((r) => {
        const shared = [r.sharing?.client_name && "Name", r.sharing?.client_mobile && "Mobile", r.sharing?.client_email && "Email"].filter(Boolean);
        return (
          <div key={r.id} data-testid={`sent-card-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0F2C59] text-white">{TYPE_LABEL[r.referral_type] || "Buyer / Renter"}</span>
              <div className="flex items-center gap-2"><StatusPill s={r.status} /><span className="text-xs text-slate-400">Sent {timeAgo(r.created_at)}</span></div>
            </div>
            {r.professional && <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5"><UserCheck className="h-4 w-4 text-primary" /> Referred to <span className="font-semibold text-slate-800">{r.professional.name}</span></p>}
            <OpportunitySummary r={r} />
            {r.note && <p className="text-sm text-slate-600 mt-2 italic">“{r.note}”</p>}
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="text-xs text-slate-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {shared.length ? `Shared after acceptance: ${shared.join(", ")}` : "No client contact shared"}</span>
              {r.status === "SUBMITTED" && (
                <button data-testid={`sent-cancel-${r.id}`} onClick={() => cancel(r.id)} className="text-xs text-red-600 hover:underline">Cancel referral</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProfessionalReferrals() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "sent" ? "sent" : "received";
  const setTab = (t) => setParams(t === "sent" ? { tab: "sent" } : {}, { replace: true });
  return (
    <PageShell testid="referrals-pro-page">
      <PageBanner
        testid="referrals-pro-banner"
        image={PAGE_BANNERS.referrals}
        icon={Handshake}
        title="Referrals"
        description="Client opportunities you send to and receive from other professionals."
      />
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 mb-6">
        <button data-testid="referrals-tab-received" onClick={() => setTab("received")} className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === "received" ? "bg-[#0F2C59] text-white" : "text-slate-600 hover:text-slate-900"}`}>Received</button>
        <button data-testid="referrals-tab-sent" onClick={() => setTab("sent")} className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${tab === "sent" ? "bg-[#0F2C59] text-white" : "text-slate-600 hover:text-slate-900"}`}>Sent</button>
      </div>
      {tab === "received" ? <ReceivedTab /> : <SentTab />}
    </PageShell>
  );
}

export default function Referrals() {
  const { user } = useAuth();
  if (!user) return null;
  const at = user.account_type || "PROFESSIONAL";
  if (at === "CONSUMER") return <Navigate to="/" replace />;
  if (at === "REFERRAL_PARTNER") return <PartnerView />;
  return <ProfessionalReferrals />;
}
